import React, { useState, useEffect } from "react";
import {
  Button,
  Col,
  Container,
  Input,
  Label,
  Row,
  FormGroup,
  FormFeedback,
  Badge,
  Card,
  CardBody,
  Spinner,
} from "reactstrap";
import { Formik, Form, Field, useField } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import { CustomButton } from "components/CommonInputs/CustomButtons/CustomButton";
import { post, get, put } from "helpers/api_helper";
import * as api from "../../constants/api_constants";
import { showErrorToast, showSuccessToast } from "helpers/toastMessages";

// Department data from CreateNewJob
const designationOptions = {
  "Software Development & Engineering": [
    "Chief Technology Officer (CTO)",
    "VP of Engineering",
    "Software Architect",
    "Engineering Manager",
    "Lead Software Engineer",
    "Senior Software Engineer",
    "Software Engineer",
    "Junior Software Engineer",
    "Frontend Developer",
    "Backend Developer",
    "Full Stack Developer",
    "Mobile App Developer",
    "DevOps Engineer",
    "others",
  ],
  "Product Management": [
    "Chief Product Officer (CPO)",
    "VP of Product",
    "Product Manager",
    "Associate Product Manager",
    "Product Owner",
    "others",
  ],
  "Quality Assurance (QA) & Testing": [
    "QA Manager",
    "Senior QA Engineer",
    "QA Engineer",
    "Automation Tester",
    "Manual Tester",
    "others",
  ],
  "UI/UX Design": [
    "UI/UX Director",
    "UX Designer",
    "UI Designer",
    "Graphic Designer",
    "Interaction Designer",
    "others",
  ],
  "IT & Infrastructure": [
    "Chief Information Officer (CIO)",
    "IT Manager",
    "System Administrator",
    "Network Engineer",
    "IT Support Specialist",
    "others",
  ],
  "Data Science & Analytics": [
    "Data Scientist",
    "Data Engineer",
    "Data Analyst",
    "Business Intelligence Analyst",
    "others",
  ],
  Cybersecurity: [
    "Chief Information Security Officer (CISO)",
    "Security Engineer",
    "Security Analyst",
    "Ethical Hacker",
    "others",
  ],
  "Sales & Business Development": [
    "Chief Revenue Officer (CRO)",
    "VP of Sales",
    "Sales Manager",
    "Business Development Manager",
    "Account Executive",
    "Sales Representative",
    "others",
  ],
  "Marketing & Digital Growth": [
    "Chief Marketing Officer (CMO)",
    "VP of Marketing",
    "Digital Marketing Manager",
    "SEO Specialist",
    "Content Marketer",
    "Social Media Manager",
    "others",
  ],
  "Human Resources (HR)": [
    "Chief Human Resources Officer (CHRO)",
    "HR Manager",
    "Talent Acquisition Specialist",
    "HR Executive",
    "others",
  ],
  "Finance & Accounting": [
    "Chief Financial Officer (CFO)",
    "Finance Manager",
    "Accountant",
    "Financial Analyst",
    "others",
  ],
  "Customer Support & Success": [
    "VP of Customer Success",
    "Customer Support Manager",
    "Technical Support Engineer",
    "Customer Success Manager",
    "others",
  ],
};

const departments = Object.keys(designationOptions).map((dept) => ({
  value: dept,
  label: dept,
}));

// Custom Input Field Wrapper
const FormikInput = ({ label, id, ...props }) => {
  const [field, meta] = useField(props);
  return (
    <FormGroup>
      {label && (
        <Label htmlFor={id}>
          {label}
          {props.required && <span className="text-danger">*</span>}
        </Label>
      )}
      <Input
        id={id}
        {...field}
        {...props}
        invalid={meta.touched && !!meta.error}
      />
      <FormFeedback>{meta.error}</FormFeedback>
    </FormGroup>
  );
};

const GET_ALL_VENDORS_LIGHT_API = "/vendor/all";

const Existingnewjob = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tId } = location.state || {};
  const { JobId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [templateData, setTemplateData] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [employees, setEmployees] = useState([]);
  const createdDate = new Date().toISOString();

  // Fetch vendors from API
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        console.log("Fetching vendors from:", GET_ALL_VENDORS_LIGHT_API);
        const response = await get(GET_ALL_VENDORS_LIGHT_API);
        console.log("Vendors API Response:", response);
        const vendorData = response?.data || [];
        setVendors(
          vendorData.map((vendor) => ({
            value: vendor.id,
            label: vendor.companyName,
          }))
        );
      } catch (error) {
        console.error("Error fetching vendors:", {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
        });
        showErrorToast("Failed to fetch vendors");
        setVendors([]);
      }
    };
    fetchVendors();
  }, []);

  // Fetch employees from API
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        console.log(
          "Fetching employees from:",
          api.GET_ALL_EMPLOYEES_NAME_ID_MAIL
        );
        const response = await get(api.GET_ALL_EMPLOYEES_NAME_ID_MAIL);
        console.log("Employees API Response:", response);
        const employeeResponse = response.map((item) => ({
          value: item.employeeId,
          label: `${item.firstName} ${item.lastName}`,
        }));
        setEmployees(employeeResponse);
      } catch (error) {
        console.error("Error fetching employees:", {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
        });
        showErrorToast("Failed to fetch employees data");
        setEmployees([]);
      }
    };
    fetchEmployees();
  }, []);

  // Fetch template data if tId is provided
  useEffect(() => {
    if (tId) {
      const fetchTemplate = async () => {
        try {
          setIsLoading(true);
          console.log("Fetching template for tId:", tId);
          const response = await get(api.GET_TEMPLATE_BY_ID(tId));
          console.log("Template API Response:", response);
          const data = response?.data || {};

          // Parse experienceRequired and salaryRange
          const [experienceRequiredFrom, experienceRequiredTo] = data.experienceRequired
            ? data.experienceRequired.split("-").map((s) => Number(s) || "")
            : ["", ""];
          const [fromSalary, toSalary] = data.salaryRange
            ? data.salaryRange.split("-").map((s) => s.replace(/[^0-9]/g, ""))
            : ["", ""];

          setTemplateData({
            templateId: data.templateId || "",
            templateName: data.templateName || "",
            jobTitle: data.jobTitle || "",
            department: data.department || "",
            workType: data.workType || "",
            jobLevel: data.jobLevel || "",
            experienceRequiredFrom: experienceRequiredFrom || "",
            experienceRequiredTo: experienceRequiredTo || "",
            payType: data.payType || "",
            fromSalary: fromSalary || "",
            toSalary: toSalary || "",
            immediateJoiningRequired: data.immediateJoining || "",
            jobDescription: data.jobDescription || "",
            companyOverview: data.companyOverview || "",
            companyUrl: data.companyUrl || "",
            perksAndBenefits: data.perksAndBenefits || "",
            rolesAndResponsibilities: data.rolesResponsibilities || "",
            selectSkills: data.requiredSkills || [],
            selectPreferredSkills: data.preferredSkills || [],
            otherRequiredSkills: data.otherRequiredSkills || "",
            otherPreferredSkills: data.otherPreferredSkills || "",
            educationQualification: data.educationalQualification || "",
            documentsToBeSubmitted: data.documentsToBeSubmitted || [],
            deadlineToApply: data.deadlineToApply || "",
            selectTheInterviewRounds: data.interviewStages || [],
            visibility: data.visibility || [],
            postOnCareerPage: data.postOnCareerPage || "",
            selectVendors: data.selectVendors || [],
            selectTeam: data.selectTeam || [],
            delFlag: data.delFlag || 1,
            createBy: data.createBy || "admin@example.com",
            jobLocation: data.jobLocation || "",
            noOfOpenings: data.noOfOpenings || "",
            employmentDuration: data.employmentDuration || "",
          });
        } catch (error) {
          console.error("Error fetching template:", {
            message: error.message,
            status: error.response?.status,
            data: error.response?.data,
          });
          showErrorToast("Failed to fetch template data");
        } finally {
          setIsLoading(false);
        }
      };
      fetchTemplate();
    }
  }, [tId]);

  // Fetch job data if JobId is provided
  useEffect(() => {
    if (JobId) {
      const fetchJobData = async () => {
        try {
          setIsLoading(true);
          console.log("Fetching job data for JobId:", JobId);
          const response = await get(api.JOBS_GET_BY_ID(JobId));
          console.log("Job API Response:", response);
          const jobData = response?.data?.jobsDetails || response?.data;

          if (!jobData) {
            throw new Error("No job data found in response");
          }

          const [experienceRequiredFrom, experienceRequiredTo] = jobData.experienceRequired
            ? jobData.experienceRequired.split("-").map((s) => Number(s) || "")
            : ["", ""];
          const [fromSalary, toSalary] = jobData.salaryRange
            ? jobData.salaryRange.split("-").map((s) => s.replace(/[^0-9]/g, ""))
            : ["", ""];

          setTemplateData({
            templateId: jobData.templateId || "",
            templateName: jobData.templateName || "",
            jobTitle: jobData.jobTitle || "",
            department: jobData.department || "",
            workType: jobData.workType || "",
            jobLevel: jobData.jobLevel || "",
            experienceRequiredFrom: experienceRequiredFrom || "",
            experienceRequiredTo: experienceRequiredTo || "",
            payType: jobData.payType || "",
            fromSalary: fromSalary || "",
            toSalary: toSalary || "",
            immediateJoiningRequired: jobData.immediateJoining || "",
            jobDescription: jobData.jobDescription || "",
            companyOverview: jobData.companyOverview || "",
            companyUrl: jobData.companyUrl || "",
            perksAndBenefits: jobData.perksAndBenefits || "",
            rolesAndResponsibilities: jobData.rolesResponsibilities || "",
            selectSkills: jobData.requiredSkills || [],
            selectPreferredSkills: jobData.preferredSkills || [],
            otherRequiredSkills: jobData.otherRequiredSkills || "",
            otherPreferredSkills: jobData.otherPreferredSkills || "",
            educationQualification: jobData.educationalQualification || "",
            documentsToBeSubmitted: jobData.documentsToBeSubmitted || [],
            deadlineToApply: jobData.deadLineToApply || "",
            selectTheInterviewRounds: jobData.interviewStages || [],
            visibility: jobData.visibility || [],
            postOnCareerPage: jobData.postOnCareerPage || "",
            selectVendors: jobData.selectVendors || [],
            selectTeam: jobData.selectTeam || [],
            delFlag: jobData.delFlag || 1,
            createBy: jobData.createBy || "admin@example.com",
            jobLocation: jobData.jobLocation || "",
            noOfOpenings: jobData.noOfOpenings || "",
            employmentDuration: jobData.employmentDuration || "",
            saveAsTemplate: !!jobData.templateName,
          });
        } catch (error) {
          console.error("Error fetching job data:", {
            message: error.message,
            status: error.response?.status,
            data: error.response?.data,
          });
          showErrorToast("Failed to fetch job data");
        } finally {
          setIsLoading(false);
        }
      };
      fetchJobData();
    }
  }, [JobId]);

  // Initial form values, prefilled with template or job data if available
  const initialValues = templateData || {
    jobTitle: "",
    department: "",
    workType: "",
    jobLevel: "",
    experienceRequiredFrom: "",
    experienceRequiredTo: "",
    jobLocation: "",
    noOfOpenings: "",
    employmentDuration: "",
    payType: "",
    fromSalary: "",
    toSalary: "",
    immediateJoiningRequired: "",
    jobDescription: "",
    companyOverview: "",
    companyUrl: "",
    perksAndBenefits: "",
    rolesAndResponsibilities: "",
    selectSkills: [],
    selectPreferredSkills: [],
    otherRequiredSkills: "",
    otherPreferredSkills: "",
    educationQualification: "",
    documentsToBeSubmitted: [],
    deadlineToApply: "",
    selectTheInterviewRounds: [],
    visibility: [],
    postOnCareerPage: "",
    selectVendors: [],
    selectTeam: [],
    saveAsTemplate: false,
    templateName: "",
    delFlag: 1,
    createBy: "admin@example.com",
  };

  // Validation schema
  const validationSchema = Yup.object().shape({
    jobTitle: Yup.string().required("Job Title is required"),
    department: Yup.string().required("Department is required"),
    workType: Yup.string().required("Work Type is required"),
    jobLevel: Yup.string().required("Job Level is required"),
    experienceRequiredFrom: Yup.number()
      .required("Minimum Experience is required")
      .min(0, "Experience must be non-negative"),
    experienceRequiredTo: Yup.number()
      .min(
        Yup.ref("experienceRequiredFrom"),
        "Maximum experience must be greater than or equal to minimum experience"
      )
      .nullable()
      .notRequired(),
    jobLocation: Yup.string().required("Job Location is required"),
    noOfOpenings: Yup.number()
      .required("Number of Openings is required")
      .min(1, "At least one opening is required"),
    employmentDuration: Yup.string().when("workType", {
      is: (val) => val === "CONTRACTOR",
      then: (schema) => schema.required("Employment Duration is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    payType: Yup.string().required("Pay Type is required"),
    // fromSalary: Yup.number()
    //   .required("Minimum Salary is required")
    //   .min(0, "Salary must be non-negative"),
    // toSalary: Yup.number()
    //   .min(
    //     Yup.ref("fromSalary"),
    //     "Maximum salary must be greater than or equal to minimum salary"
    //   )
    //   .nullable()
    //   .notRequired(),
    immediateJoiningRequired: Yup.string().required("Immediate Joining is required"),
    jobDescription: Yup.string().required("Job Description is required"),
    companyOverview: Yup.string().required("Company Overview is required"),
    companyUrl: Yup.string()
      .url("Must be a valid URL")
      .required("Company URL is required"),
    perksAndBenefits: Yup.string().required("Perks and Benefits is required"),
    rolesAndResponsibilities: Yup.string().required("Roles & Responsibilities is required"),
    selectSkills: Yup.array()
      .min(1, "At least one required skill is required")
      .required("Required Skills are required"),
    selectPreferredSkills: Yup.array().notRequired(),
    otherRequiredSkills: Yup.string().notRequired(),
    otherPreferredSkills: Yup.string().notRequired(),
    educationQualification: Yup.string().required("Educational Qualification is required"),
    documentsToBeSubmitted: Yup.array()
      .min(1, "At least one document is required")
      .required("Documents are required"),
    // deadlineToApply: Yup.string()
    //   .matches(/^\d{4}-\d{2}-\d{2}$/, "Must be a valid date (YYYY-MM-DD)")
    //   .required("Deadline to Apply is required"),
    selectTheInterviewRounds: Yup.array()
      .min(1, "At least one interview round must be selected")
      .required("Interview Rounds are required"),
    postOnCareerPage: Yup.string().required("Careers Page option is required"),
    selectVendors: Yup.array().when("visibility", {
      is: (val) => val && val.includes("Vendors"),
      then: (schema) =>
        schema
          .min(1, "At least one vendor must be selected")
          .required("Vendors are required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    selectTeam: Yup.array()
      .min(1, "At least one team member must be selected")
      .required("Team is required"),
    saveAsTemplate: Yup.boolean(),
    templateName: Yup.string().when("saveAsTemplate", {
      is: true,
      then: (schema) => schema.required("Template Name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    delFlag: Yup.number().default(1),
    createBy: Yup.string().default("admin@example.com"),
  });

  // Generic multi-select handlers
  const handleSelect = (e, setFieldValue, fieldName, currentValues) => {
    const value = e.target.value;
    if (value && !currentValues.includes(value)) {
      setFieldValue(fieldName, [...currentValues, value]);
    }
    e.target.value = "";
  };

  const removeItem = (itemToRemove, setFieldValue, fieldName, currentValues) => {
    setFieldValue(
      fieldName,
      currentValues.filter((item) => item !== itemToRemove)
    );
  };

  const handleCheckboxChange = (
    item,
    checked,
    setFieldValue,
    fieldName,
    currentValues
  ) => {
    const updatedValues = checked
      ? [...currentValues, item]
      : currentValues.filter((i) => i !== item);
    setFieldValue(fieldName, updatedValues);
  };

  // Handler for skills input with comma separation
  const handleSkillsInput = (e, setFieldValue, fieldName, currentSkills) => {
    const input = e.target.value;
    if (input.includes(",")) {
      const newSkills = input
        .split(",")
        .map((skill) => skill.trim())
        .filter((skill) => skill && !currentSkills.includes(skill));
      if (newSkills.length > 0) {
        setFieldValue(fieldName, [...currentSkills, ...newSkills]);
      }
      e.target.value = "";
    }
  };

  // onSubmit handler with payload for both job and template
  const handleSubmit = async (values, { setSubmitting }) => {
    const jobPayload = {
      jobsDetails: {
        jobTitle: values.jobTitle,
        department: values.department,
        workType: values.workType,
        jobLevel: values.jobLevel,
        experienceRequired: values.experienceRequiredTo
          ? `${values.experienceRequiredFrom}-${values.experienceRequiredTo}`
          : `${values.experienceRequiredFrom}`,
        jobLocation: values.jobLocation,
        noOfOpenings: values.noOfOpenings,
        employmentDuration: values.employmentDuration,
        payType: values.payType,
        salaryRange: values.toSalary
          ? `${values.fromSalary}-${values.toSalary}`
          : `${values.fromSalary}`,
        immediateJoining: values.immediateJoiningRequired,
        jobDescription: values.jobDescription,
        companyOverview: values.companyOverview,
        companyUrl: values.companyUrl,
        perksAndBenefits: values.perksAndBenefits,
        rolesResponsibilities: values.rolesAndResponsibilities,
        requiredSkills: values.selectSkills,
        preferredSkills: values.selectPreferredSkills,
        otherRequiredSkills: values.otherRequiredSkills,
        otherPreferredSkills: values.otherPreferredSkills,
        educationalQualification: values.educationQualification,
        documentsToBeSubmitted: values.documentsToBeSubmitted,
        deadLineToApply: values.deadlineToApply,
        interviewStages: values.selectTheInterviewRounds,
        postOnCareerPage: values.postOnCareerPage,
        selectVendors: values.selectVendors,
        selectTeam: values.selectTeam,
        delFlag: values.delFlag,
        createBy: values.createBy,
        ...(tId && { templateId: tId }),
      },
    };

    const templatePayload = {
      templateDetails: {
        templateName: values.templateName,
        jobTitle: values.jobTitle,
        department: values.department,
        workType: values.workType,
        jobLevel: values.jobLevel,
        experienceRequired: values.experienceRequiredTo
          ? `${values.experienceRequiredFrom}-${values.experienceRequiredTo}`
          : `${values.experienceRequiredFrom}`,
        jobLocation: values.jobLocation,
        noOfOpenings: values.noOfOpenings,
        employmentDuration: values.employmentDuration,
        payType: values.payType,
        salaryRange: values.toSalary
          ? `${values.fromSalary}-${values.toSalary}`
          : `${values.fromSalary}`,
        immediateJoining: values.immediateJoiningRequired,
        jobDescription: values.jobDescription,
        companyOverview: values.companyOverview,
        companyUrl: values.companyUrl,
        perksAndBenefits: values.perksAndBenefits,
        rolesResponsibilities: values.rolesAndResponsibilities,
        requiredSkills: values.selectSkills,
        preferredSkills: values.selectPreferredSkills,
        otherRequiredSkills: values.otherRequiredSkills,
        otherPreferredSkills: values.otherPreferredSkills,
        educationalQualification: values.educationQualification,
        documentsToBeSubmitted: values.documentsToBeSubmitted,
        deadLineToApply: values.deadlineToApply,
        interviewStages: values.selectTheInterviewRounds,
        createBy: values.createBy,
      },
    };

    try {
      setIsLoading(true);

      // Handle job creation or update
      if (JobId) {
        console.log("Updating job with payload:", jobPayload);
        await put(api.UPDATE_JOB_BY_ID(JobId), jobPayload);
        showSuccessToast("Job updated successfully");
      } else {
        console.log("Creating job with payload:", jobPayload);
        await post(api.CREATE_JOB_POST, jobPayload);
        showSuccessToast("Job created successfully");
      }

      // Save as template if checked
      if (values.saveAsTemplate) {
        console.log("Saving template with payload:", templatePayload);
        await post(api.CREATE_TEMPLATE, templatePayload);
        showSuccessToast("Template saved successfully");
      }

      navigate("/job-postings");
    } catch (error) {
      console.error("Error submitting form:", {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      });
      showErrorToast(
        error.name === "AbortError"
          ? "Request timed out"
          : error.response?.data?.message || "Something went wrong"
      );
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  // Improved loading UI
  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p>Loading {JobId ? "job" : "template"} data...</p>
      </div>
    );
  }

  return (
    <div className="page-content">
      <PageSubHeader
        heading={JobId ? "Edit Job Post" : "New Job Post"}
        routePath={"Recruitment"}
        pageName={
          JobId ? "Job Posting/Edit Job Post" : "Job Posting/New Job Post"
        }
      />
      <Container fluid className="py-4 px-5">
        <Row>
          <Col>
            <Card>
              <CardBody>
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({ handleChange, setFieldValue, values, touched, errors }) => (
                    <Form className="row g-3 needs-validation py-0">
                      {/* General Info */}
                      <Row className="heading mt-3 pe-4">
                        <p className="sub-head">01 - General</p>
                      </Row>
                      <Row>
                        <Col md={4}>
                          <FormikInput
                            label="Template Name"
                            id="templateName"
                            name="templateName"
                            placeholder="Template Name"
                            disabled={!values.saveAsTemplate}
                            required={values.saveAsTemplate}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col md={4}>
                          <FormikInput
                            label="Job ID"
                            id="jobId"
                            name="jobId"
                            placeholder="Job ID (auto-generated)"
                            value={JobId || ""}
                            disabled
                          />
                        </Col>
                        <Col md={4}>
                          <FormikInput
                            label="Job Title"
                            id="jobTitle"
                            name="jobTitle"
                            placeholder="Job Title"
                            required
                          />
                        </Col>
                        <Col md={4}>
                          <FormGroup>
                            <Label htmlFor="department">
                              Department<span className="text-danger">*</span>
                            </Label>
                            <Field name="department" id="department">
                              {({ field, meta }) => (
                                <>
                                  <Input
                                    type="select"
                                    {...field}
                                    invalid={meta.touched && !!meta.error}
                                    onChange={handleChange}
                                  >
                                    <option value="">Select</option>
                                    {departments.map((dept) => (
                                      <option
                                        key={dept.value}
                                        value={dept.value}
                                      >
                                        {dept.label}
                                      </option>
                                    ))}
                                  </Input>
                                  <FormFeedback>{meta.error}</FormFeedback>
                                </>
                              )}
                            </Field>
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={4}>
                          <FormGroup>
                            <Label htmlFor="workType">
                              Work Type<span className="text-danger">*</span>
                            </Label>
                            <Field name="workType" id="workType">
                              {({ field, meta }) => (
                                <>
                                  <Input
                                    type="select"
                                    {...field}
                                    invalid={meta.touched && !!meta.error}
                                    onChange={handleChange}
                                  >
                                    <option value="">Select</option>
                                    <option value="C2H">C2H (Contract to Hire)</option>
                                    <option value="CONSULTANT">Consultant</option>
                                    <option value="CONTRACTOR">Contractor</option>
                                    <option value="FULL_TIME">Full Time</option>
                                    <option value="INTERN">Intern</option>
                                    <option value="PART_TIME">Part Time</option>
                                    <option value="TEMPORARY">Temporary</option>
                                    <option value="HYBRID">Hybrid</option>
                                  </Input>
                                  <FormFeedback>{meta.error}</FormFeedback>
                                </>
                              )}
                            </Field>
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup>
                            <Label htmlFor="jobLevel">
                              Job Level<span className="text-danger">*</span>
                            </Label>
                            <Field name="jobLevel" id="jobLevel">
                              {({ field, meta }) => (
                                <>
                                  <Input
                                    type="select"
                                    {...field}
                                    invalid={meta.touched && !!meta.error}
                                    onChange={handleChange}
                                  >
                                    <option value="">Select</option>
                                    <option value="Entry-level">Entry-level</option>
                                    <option value="Mid-level">Mid-level</option>
                                    <option value="Senior">Senior</option>
                                    <option value="Executive">Executive</option>
                                  </Input>
                                  <FormFeedback>{meta.error}</FormFeedback>
                                </>
                              )}
                            </Field>
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup>
                            <Label htmlFor="experienceRequiredFrom">
                              Experience<span className="text-danger">*</span>
                            </Label>
                            <Field name="experienceRequiredFrom" id="experienceRequiredFrom">
                              {({ field, meta }) => (
                                <>
                                  <Input
                                    type="text"
                                    {...field}
                                    placeholder="Minimum"
                                    invalid={meta.touched && !!meta.error}
                                  />
                                  <FormFeedback>{meta.error}</FormFeedback>
                                </>
                              )}
                            </Field>
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        
                        <Col md={4}>
                          <FormGroup>
                            <Label htmlFor="jobLocation">
                              Job Location<span className="text-danger">*</span>
                            </Label>
                            <Field name="jobLocation" id="jobLocation">
                              {({ field, meta }) => (
                                <>
                                  <Input
                                    type="text"
                                    {...field}
                                    placeholder="e.g., Bangalore, Remote"
                                    invalid={meta.touched && !!meta.error}
                                  />
                                  <FormFeedback>{meta.error}</FormFeedback>
                                </>
                              )}
                            </Field>
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup>
                            <Label htmlFor="noOfOpenings">
                              No. of Openings<span className="text-danger">*</span>
                            </Label>
                            <Field name="noOfOpenings" id="noOfOpenings">
                              {({ field, meta }) => (
                                <>
                                  <Input
                                    type="number"
                                    {...field}
                                    placeholder="e.g., 2"
                                    invalid={meta.touched && !!meta.error}
                                  />
                                  <FormFeedback>{meta.error}</FormFeedback>
                                </>
                              )}
                            </Field>
                          </FormGroup>
                        </Col>
                          <Col md={4}>
                          <FormGroup>
                            <Label htmlFor="employmentDuration">
                              Employment Duration (for contract-based roles)
                            </Label>
                            <Field name="employmentDuration" id="employmentDuration">
                              {({ field, meta }) => (
                                <>
                                  <Input
                                    type="text"
                                    {...field}
                                    placeholder="e.g., 6 months, 1 year"
                                    invalid={meta.touched && !!meta.error}
                                  >
                                   
                                  </Input>
                                  <FormFeedback>{meta.error}</FormFeedback>
                                </>
                              )}
                            </Field>
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                      
                        <Col md={4}>
                          <FormGroup>
                            <Label htmlFor="payType">
                              Pay Type<span className="text-danger">*</span>
                            </Label>
                            <Field name="payType" id="payType">
                              {({ field, meta }) => (
                                <>
                                  <Input
                                    type="text"
                                    {...field}
                                    placeholder="Pay Rate"
                                    invalid={meta.touched && !!meta.error}
                                  >
                                    
                                  </Input>
                                  <FormFeedback>{meta.error}</FormFeedback>
                                </>
                              )}
                            </Field>
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup>
                            <Label htmlFor="immediateJoiningRequired">
                              Immediate Joining Required?<span className="text-danger">*</span>
                            </Label>
                            <Field name="immediateJoiningRequired" id="immediateJoiningRequired">
                              {({ field, meta }) => (
                                <>
                                  <Input
                                    type="select"
                                    {...field}
                                    invalid={meta.touched && !!meta.error}
                                  >
                                    <option value="">Select</option>
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                  </Input>
                                  <FormFeedback>{meta.error}</FormFeedback>
                                </>
                              )}
                            </Field>
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup>
                            <Label htmlFor="fromSalary">
                              Salary Range<span className="text-danger">*</span>
                            </Label>
                            <Field name="fromSalary" id="fromSalary">
                              {({ field, meta }) => (
                                <>
                                  <Input
                                    type="text"
                                    {...field}
                                    placeholder="e.g., 6-8 LPA, 1000 USD, 600000 INR"
                                    invalid={meta.touched && !!meta.error}
                                  />
                                  <FormFeedback>{meta.error}</FormFeedback>
                                </>
                              )}
                            </Field>
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        
                        
                      </Row>

                      {/* Job Description */}
                      <Row className="heading mt-3 pe-4">
                        <p className="sub-head">02 - Job Description</p>
                      </Row>
                      <Row>
                        <Col>
                          <FormikInput
                            label="Job Description"
                            id="jobDescription"
                            name="jobDescription"
                            type="textarea"
                            placeholder="Describe the job responsibilities..."
                            rows={5}
                            required
                          />
                        </Col>
                      </Row>

                      {/* Company Overview */}
                      <Row className="heading mt-3 pe-4">
                        <p className="sub-head">03 - Company Overview</p>
                      </Row>
                      <Row>
                        <Col>
                          <FormikInput
                            label="Company Overview"
                            id="companyOverview"
                            name="companyOverview"
                            type="textarea"
                            placeholder="Provide an overview of the company..."
                            rows={5}
                            required
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col md={6}>
                          <FormikInput
                            label="Company URL"
                            id="companyUrl"
                            name="companyUrl"
                            placeholder="e.g., https://example.com"
                            required
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <FormikInput
                            label="Perks & Benefits"
                            id="perksAndBenefits"
                            name="perksAndBenefits"
                            type="textarea"
                            placeholder="List perks and benefits..."
                            rows={5}
                            required
                          />
                        </Col>
                      </Row>

                      {/* Roles & Responsibilities */}
                      <Row className="heading mt-3 pe-4">
                        <p className="sub-head">04 - Roles & Responsibilities</p>
                      </Row>
                      <Row>
                        <Col>
                          <FormikInput
                            label="Roles & Responsibilities"
                            id="rolesAndResponsibilities"
                            name="rolesAndResponsibilities"
                            type="textarea"
                            placeholder="Detail the roles and responsibilities..."
                            rows={5}
                            required
                          />
                        </Col>
                      </Row>

                      {/* Required Skills */}
                      <Row className="heading mt-3 pe-4">
                        <p className="sub-head">05 - Required Skills</p>
                      </Row>
                      <Row>
                        <Col md={4}>
                          <FormGroup>
                            <Label htmlFor="selectSkills">
                              Enter Skills (comma-separated)<span className="text-danger">*</span>
                            </Label>
                            <Input
                              type="text"
                              id="selectSkills"
                              placeholder="e.g., Java, Python, SQL"
                              onChange={(e) =>
                                handleSkillsInput(e, setFieldValue, "selectSkills", values.selectSkills)
                              }
                              onBlur={() => setFieldValue("selectSkills", values.selectSkills)}
                              invalid={touched.selectSkills && !!errors.selectSkills}
                            />
                            <FormFeedback>{errors.selectSkills}</FormFeedback>
                          </FormGroup>
                          <div className="mt-3">
                            {values.selectSkills.map((skill) => (
                              <Badge
                                key={skill}
                                pill
                                className="me-2 mb-2"
                                style={{
                                  cursor: "pointer",
                                  padding: "6px 12px",
                                  fontSize: "14px",
                                  color: "black",
                                  backgroundColor: "#EFEFEF",
                                }}
                                onClick={() =>
                                  removeItem(skill, setFieldValue, "selectSkills", values.selectSkills)
                                }
                              >
                                {skill} ×
                              </Badge>
                            ))}
                          </div>
                        </Col>
                      </Row>
                      {/* <Row>
                        <Col>
                          <FormikInput
                            label="Other Required Skills"
                            id="otherRequiredSkills"
                            name="otherRequiredSkills"
                            type="textarea"
                            placeholder="List any additional skills..."
                            rows={3}
                          />
                        </Col>
                      </Row> */}

                      {/* Preferred Skills */}
                      <Row className="heading mt-3 pe-4">
                        <p className="sub-head">06 - Preferred Skills</p>
                      </Row>
                      <Row>
                        <Col md={4}>
                          <FormGroup>
                            <Label htmlFor="selectPreferredSkills">
                              Enter Preferred Skills (comma-separated)
                            </Label>
                            <Input
                              type="text"
                              id="selectPreferredSkills"
                              placeholder="e.g., React, Node.js"
                              onChange={(e) =>
                                handleSkillsInput(e, setFieldValue, "selectPreferredSkills", values.selectPreferredSkills)
                              }
                              onBlur={() => setFieldValue("selectPreferredSkills", values.selectPreferredSkills)}
                              invalid={touched.selectPreferredSkills && !!errors.selectPreferredSkills}
                            />
                            <FormFeedback>{errors.selectPreferredSkills}</FormFeedback>
                          </FormGroup>
                          <div className="mt-3">
                            {values.selectPreferredSkills.map((skill) => (
                              <Badge
                                key={skill}
                                pill
                                className="me-2 mb-2"
                                style={{
                                  cursor: "pointer",
                                  padding: "6px 12px",
                                  fontSize: "14px",
                                  color: "black",
                                  backgroundColor: "#EFEFEF",
                                }}
                                onClick={() =>
                                  removeItem(skill, setFieldValue, "selectPreferredSkills", values.selectPreferredSkills)
                                }
                              >
                                {skill} ×
                              </Badge>
                            ))}
                          </div>
                        </Col>
                      </Row>
                      {/* <Row>
                        <Col>
                          <FormikInput
                            label="Other Preferred Skills"
                            id="otherPreferredSkills"
                            name="otherPreferredSkills"
                            type="textarea"
                            placeholder="List any additional preferred skills..."
                            rows={3}
                          />
                        </Col>
                      </Row> */}

                      {/* Educational Qualification */}
                      <Row className="heading mt-3 pe-4">
                        <p className="sub-head">07 - Educational Qualification</p>
                      </Row>
                      <Row>
                        <Col md={4}>
                          <FormGroup>
                            <Label htmlFor="educationQualification">
                              Educational Qualification<span className="text-danger">*</span>
                            </Label>
                            <Field name="educationQualification" id="educationQualification">
                              {({ field, meta }) => (
                                <>
                                  <Input
                                    type="text"
                                    {...field}
                                    placeholder="e.g., Bachelor's, Master's"
                                    invalid={meta.touched && !!meta.error}
                                  >
                                   
                                  </Input>
                                  <FormFeedback>{meta.error}</FormFeedback>
                                </>
                              )}
                            </Field>
                          </FormGroup>
                        </Col>
                      </Row>

                      {/* Application Requirements */}
                      <Row className="heading mt-3 pe-4">
                        <p className="sub-head">08 - Application Requirements</p>
                      </Row>
                      <Row>
                        <p
                          style={{
                            color: "#222222",
                            fontWeight: 700,
                            fontSize: "14px",
                            lineHeight: "20px",
                          }}
                        >
                          Documents To Be Submitted<span className="text-danger">*</span>
                        </p>
                        <Col>
                          {[
                            "Resume",
                            "ID Proof",
                            "Certificates",
                            "Cover Letter",
                            "Passport",
                            "Visa",
                          ].map((doc) => (
                            <FormGroup className="d-flex gap-2" key={doc}>
                              <Input
                                className="checkbox"
                                type="checkbox"
                                id={`doc-${doc}`}
                                style={{ border: "1px solid #475467" }}
                                checked={values.documentsToBeSubmitted.includes(doc)}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    doc,
                                    e.target.checked,
                                    setFieldValue,
                                    "documentsToBeSubmitted",
                                    values.documentsToBeSubmitted
                                  )
                                }
                              />
                              <Label
                                htmlFor={`doc-${doc}`}
                                style={{
                                  fontFamily: "Inter, sans-serif",
                                  fontWeight: 500,
                                  fontSize: "14px",
                                  lineHeight: "22px",
                                }}
                              >
                                {doc}
                              </Label>
                            </FormGroup>
                          ))}
                          {touched.documentsToBeSubmitted && errors.documentsToBeSubmitted && (
                            <FormFeedback style={{ display: "block" }}>
                              {errors.documentsToBeSubmitted}
                            </FormFeedback>
                          )}
                          <FormikInput
                            label="Deadline to Apply"
                            id="deadlineToApply"
                            name="deadlineToApply"
                            type="date"
                            placeholder="YYYY-MM-DD"
                            
                          />
                        </Col>
                      </Row>

                      {/* Interview Process */}
                      <Row className="heading mt-3 pe-4">
                        <p className="sub-head">09 - Interview Process</p>
                      </Row>
                      <Row>
                        <p
                          style={{
                            color: "#222222",
                            fontWeight: 700,
                            fontSize: "14px",
                            lineHeight: "20px",
                          }}
                        >
                          Select the Interview Rounds<span className="text-danger">*</span>
                        </p>
                        <Col>
                          {[
                            "Resume Review",
                            "Technical Interview",
                            "HR Round",
                            "Application Screening / Resume Shortlisting",
                            "Assignment / Take-Home Task",
                            "Managerial / Domain Round",
                            "Client Round",
                          ].map((round) => (
                            <FormGroup className="d-flex gap-2" key={round}>
                              <Input
                                className="checkbox"
                                type="checkbox"
                                id={`round-${round.replace(/\s/g, "-")}`}
                                style={{ border: "1px solid #475467" }}
                                checked={values.selectTheInterviewRounds.includes(round)}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    round,
                                    e.target.checked,
                                    setFieldValue,
                                    "selectTheInterviewRounds",
                                    values.selectTheInterviewRounds
                                  )
                                }
                              />
                              <Label
                                htmlFor={`round-${round.replace(/\s/g, "-")}`}
                                style={{
                                  fontFamily: "Inter, sans-serif",
                                  fontWeight: 500,
                                  fontSize: "14px",
                                  lineHeight: "22px",
                                }}
                              >
                                {round}
                              </Label>
                            </FormGroup>
                          ))}
                          {touched.selectTheInterviewRounds && errors.selectTheInterviewRounds && (
                            <FormFeedback style={{ display: "block" }}>
                              {errors.selectTheInterviewRounds}
                            </FormFeedback>
                          )}
                        </Col>
                      </Row>

                      {/* Job Visibility Control */}
                      <Row className="heading mt-3 pe-4">
                        <p className="sub-head">10 - Job Visibility Control</p>
                      </Row>
                      <Row>
                        <Col>
                          {["Public", "Internal Employees", "Vendors"].map((option) => (
                            <FormGroup className="d-flex gap-2" key={option}>
                              <Input
                                className="checkbox"
                                type="checkbox"
                                id={`visibility-${option}`}
                                style={{ border: "1px solid #475467" }}
                                checked={values.visibility.includes(option)}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    option,
                                    e.target.checked,
                                    setFieldValue,
                                    "visibility",
                                    values.visibility
                                  )
                                }
                              />
                              <Label
                                htmlFor={`visibility-${option}`}
                                style={{
                                  fontFamily: "Inter, sans-serif",
                                  fontWeight: 500,
                                  fontSize: "14px",
                                  lineHeight: "20px",
                                }}
                              >
                                {option}
                              </Label>
                            </FormGroup>
                          ))}
                        </Col>
                      </Row>
                      <Row>
                        <Col md={4}>
                          <FormGroup>
                            <Label htmlFor="postOnCareerPage">
                              Post on Careers Page<span className="text-danger">*</span>
                            </Label>
                            <Field name="postOnCareerPage" id="postOnCareerPage">
                              {({ field, meta }) => (
                                <>
                                  <Input
                                    type="select"
                                    {...field}
                                    invalid={meta.touched && !!meta.error}
                                  >
                                    <option value="">Select</option>
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                  </Input>
                                  <FormFeedback>{meta.error}</FormFeedback>
                                </>
                              )}
                            </Field>
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={4}>
                          <Label htmlFor="selectVendors">
                            Select Vendors
                            {values.visibility.includes("Vendors") && (
                              <span className="text-danger">*</span>
                            )}
                          </Label>
                          <Input
                            type="select"
                            id="selectVendors"
                            onChange={(e) =>
                              handleSelect(
                                e,
                                setFieldValue,
                                "selectVendors",
                                values.selectVendors
                              )
                            }
                            disabled={isLoading}
                          >
                            <option value="">Select</option>
                            {vendors.map((vendor) => (
                              <option key={vendor.value} value={vendor.value}>
                                {vendor.label}
                              </option>
                            ))}
                          </Input>
                          {touched.selectVendors && errors.selectVendors && (
                            <FormFeedback style={{ display: "block" }}>
                              {errors.selectVendors}
                            </FormFeedback>
                          )}
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <div className="mt-3">
                            {values.selectVendors.map((vendor) => (
                              <Badge
                                key={vendor}
                                pill
                                className="me-2 mb-2"
                                style={{
                                  cursor: "pointer",
                                  padding: "6px 12px",
                                  fontSize: "14px",
                                  color: "black",
                                  backgroundColor: "#EFEFEF",
                                }}
                                onClick={() =>
                                  removeItem(
                                    vendor,
                                    setFieldValue,
                                    "selectVendors",
                                    values.selectVendors
                                  )
                                }
                              >
                                {vendors.find((v) => v.value === vendor)?.label || vendor} ×
                              </Badge>
                            ))}
                          </div>
                        </Col>
                      </Row>

                      {/* Assign to Team */}
                      <Row className="heading mt-3 pe-4">
                        <p className="sub-head">11 - Assign To Team</p>
                      </Row>
                      <Row>
                        <Col md={4}>
                          <Label htmlFor="selectTeam">
                            Select Team<span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="select"
                            id="selectTeam"
                            onChange={(e) =>
                              handleSelect(
                                e,
                                setFieldValue,
                                "selectTeam",
                                values.selectTeam
                              )
                            }
                            disabled={isLoading}
                          >
                            <option value="">Select</option>
                            {employees.map((employee) => (
                              <option key={employee.value} value={employee.value}>
                                {employee.label}
                              </option>
                            ))}
                          </Input>
                          {touched.selectTeam && errors.selectTeam && (
                            <FormFeedback style={{ display: "block" }}>
                              {errors.selectTeam}
                            </FormFeedback>
                          )}
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <div className="mt-3">
                            {values.selectTeam.map((team) => (
                              <Badge
                                key={team}
                                pill
                                className="me-2 mb-2"
                                style={{
                                  cursor: "pointer",
                                  padding: "6px 12px",
                                  fontSize: "14px",
                                  color: "black",
                                  backgroundColor: "#EFEFEF",
                                }}
                                onClick={() =>
                                  removeItem(
                                    team,
                                    setFieldValue,
                                    "selectTeam",
                                    values.selectTeam
                                  )
                                }
                              >
                                {employees.find((e) => e.value === team)?.label || team} ×
                              </Badge>
                            ))}
                          </div>
                        </Col>
                      </Row>

                      {/* Save as Template */}
                      <Row className="heading mt-3 pe-4">
                        <p className="sub-head">12 - Save as Template</p>
                      </Row>
                      <Row>
                        <Col>
                          <FormGroup className="d-flex gap-2 align-items-center">
                            <Field name="saveAsTemplate" id="saveAsTemplate">
                              {({ field }) => (
                                <Input
                                  className="checkbox"
                                  type="checkbox"
                                  style={{ border: "1px solid #475467" }}
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) =>
                                    setFieldValue("saveAsTemplate", e.target.checked)
                                  }
                                />
                              )}
                            </Field>
                            <Label
                              htmlFor="saveAsTemplate"
                              style={{
                                fontFamily: "Inter, sans-serif",
                                fontWeight: 500,
                                fontSize: "14px",
                                lineHeight: "22px",
                              }}
                            >
                              Save as Template
                            </Label>
                          </FormGroup>
                          {values.saveAsTemplate && (
                            <Col md={4}>
                              <FormikInput
                                label="Template Name"
                                id="templateName"
                                name="templateName"
                                placeholder="Template Name"
                                required
                              />
                            </Col>
                          )}
                        </Col>
                      </Row>

                      {/* Form Actions */}
                      <Row className="mt-4">
                        <Col className="gap-2 d-flex justify-content-end">
                          <Button
                            className="text-primary border-primary"
                            style={{
                              backgroundColor: "#FFFFFF",
                              color: "primary",
                              borderRadius: "6px",
                              border: "1px solid #475467",
                            }}
                            onClick={() => navigate("/job-postings")}
                          >
                            Close
                          </Button>
                          <CustomButton
                            text={JobId ? "Update" : "Submit"}
                            type="submit"
                            disabled={isLoading}
                          />
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Existingnewjob;